/**
 * Downloads a large static file once and keeps it in the Cache API (models are tens of MB), with
 * byte progress. Falls back to a plain fetch where the Cache API is unavailable.
 */
const CACHE = 'foe-names-models-v1';

export async function fetchCached(
  url: string,
  onProgress?: (loaded: number, total: number) => void,
  /** Expected SHA-256 (hex): a download that does not match is rejected and never cached. */
  sha256?: string,
): Promise<ArrayBuffer> {
  const cache =
    'caches' in globalThis ? await caches.open(CACHE).catch(() => undefined) : undefined;
  const hit = await cache?.match(url);
  if (hit) {
    const bytes = await hit.arrayBuffer();
    onProgress?.(bytes.byteLength, bytes.byteLength);
    return bytes;
  }
  const response = await fetch(url);
  if (!response.ok || !response.body) {
    throw new Error(`failed to download ${url}: HTTP ${String(response.status)}`);
  }
  const total = Number(response.headers.get('content-length') ?? 0);
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;
    onProgress?.(loaded, Math.max(total, loaded));
  }
  const bytes = new Uint8Array(loaded);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  if (sha256) {
    const actual = await digestHex(bytes);
    if (actual !== sha256) throw new Error(`${url}: SHA-256 ${actual}, expected ${sha256}`);
  }
  await cache
    ?.put(url, new Response(bytes, { headers: { 'content-type': 'application/octet-stream' } }))
    .catch(() => undefined);
  return bytes.buffer;
}

export async function digestHex(bytes: Uint8Array<ArrayBuffer>): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}
